import React, { useState, useEffect } from 'react';
import WeeklyEvents from '../components/WeeklyEvents';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Defined here to use in both the fetch and the UI link
  const CALENDAR_URL = "https://outlook.office365.com/owa/calendar/77e38992a1e34a368cfc5ac24eb78bce@helsinki.fi/1e5250d1e44d46d4b342ceaaade0d18813154901556893878411/calendar.ics";

  useEffect(() => {
    const fetchOutlookCalendar = async () => {
      try {
        // If on localhost, use the proxy (because Cloudflare functions don't run in 'npm start')
        // If on production (Cloudflare), use your internal API function (at functions/api/calendar.js).
        const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        
        const fetchUrl = isLocal 
          ? `https://corsproxy.io/?${encodeURIComponent(CALENDAR_URL + '?t=' + Date.now())}`
          : '/api/calendar';

        const response = await fetch(fetchUrl);
        
        if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);
        
        const textData = await response.text(); 
        let text = textData;

        if (!text || !text.includes("BEGIN:VCALENDAR")) {
          console.error("Invalid ICS data received");
          setLoading(false);
          return;
        }

        text = text.replace(/\r\n /g, '');
        const eventBlocks = text.split("BEGIN:VEVENT");
        eventBlocks.shift();

        // Time Windows
        const now = new Date();
        now.setDate(now.getDate() - 1); 
        now.setHours(0, 0, 0, 0);

        const twoMonthsFromNow = new Date();
        twoMonthsFromNow.setMonth(now.getMonth() + 2);

        const clean = (str) => str ? str.replace(/\\,/g, ',').replace(/\\n/g, ' ').replace(/\r/g, '').trim() : "";

        const parsed = eventBlocks.map((block, index) => {
          const summaryMatch = block.match(/SUMMARY.*?:(.*?)(\r?\n[A-Z]|$)/s);
          const startMatch = block.match(/DTSTART.*?:(\d{8}(T\d{6}Z?)?)/);
          const locationMatch = block.match(/LOCATION.*?:(.*?)(\r?\n[A-Z]|$)/s);

          if (!startMatch) return null;

          const startStr = startMatch[1];
          const year = startStr.substring(0, 4);
          const month = startStr.substring(4, 6);
          const day = startStr.substring(6, 8);

          let eventDate;
          if (startStr.includes('T')) {
            const hour = startStr.substring(9, 11);
            const min = startStr.substring(11, 13);
            eventDate = new Date(`${year}-${month}-${day}T${hour}:${min}:00`);
          } else {
            eventDate = new Date(`${year}-${month}-${day}T00:00:00`);
          }

          if (isNaN(eventDate.getTime())) return null;

          return {
            id: `event-${index}-${startStr}`,
            dayNumber: eventDate.getDate().toString().padStart(2, '0'),
            month: eventDate.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
            time: startStr.includes('T') 
                ? eventDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) 
                : "All Day",
            title: clean(summaryMatch ? summaryMatch[1] : "Untitled Event"),
            location: clean(locationMatch ? locationMatch[1] : ""),
            fullDate: eventDate
          };
        })
        .filter(ev => ev !== null && ev.fullDate >= now && ev.fullDate <= twoMonthsFromNow)
        .sort((a, b) => a.fullDate - b.fullDate);

        setEvents(parsed);

      } catch (error) {
        console.error("Connection to calendar failed:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutlookCalendar();
  }, []);

  return (
    <div className="page events-page">
      <section className="page-header">
        <div className="container">
          <h1>Events</h1>
          <p>Upcoming events and activities</p>
        </div>
      </section>
      
      <section className="content-section">
        <div className="container">
          <h2>Upcoming Events</h2>
          
          {/* New Calendar Import Text Block */}
          <div style={{ marginBottom: '20px', fontSize: '0.95rem', color: '#555' }}>
            <p style={{ marginBottom: '5px' }}>
              You can import this into your own calendar (Google Calendar, Outlook, etc.) using this link:
            </p>
            <a 
              href={CALENDAR_URL}
              style={{ 
                display: 'block',
                wordBreak: 'break-all', 
                color: '#0078d4',
                fontSize: '0.85rem',
                backgroundColor: '#f0f4f8',
                padding: '10px',
                borderRadius: '5px',
                textDecoration: 'none'
              }}
            >
              {CALENDAR_URL}
            </a>
          </div>

          <div className="events-calendar">
            {loading ? (
              <div className="loading-spinner" style={{textAlign: 'center', padding: '20px'}}>
                Connecting to University Calendar...
              </div>
            ) : (
              <WeeklyEvents events={events} />
            )}
          </div>
          
           <div className="event-types">
            <h3>Types of Events</h3>
            <div className="event-types-grid">
              <div className="event-type">
                <h4>Academic Workshops</h4>
                <p>Study sessions and academic support events</p>
              </div>
              <div className="event-type">
                <h4>Social Events</h4>
                <p>Parties, game nights, and social gatherings</p>
              </div>
              <div className="event-type">
                <h4>Company Visits</h4>
                <p>Excursions to partner companies and organizations</p>
              </div>
              <div className="event-type">
                <h4>Orientation Events</h4>
                <p>Welcome events for new students</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;